import { createEventSourceObservable } from '@core/services/offer/_private/helpers/eventSourceObservable';
import { Subscription } from 'rxjs';
import { bufferTime, filter, map } from 'rxjs/operators';
import IEventDto from '@core/mappers/offer/dtos/IEventDto';
import EventMapper from '@core/mappers/offer/EventMapper';
import StructManager from '@core/services/common/StructManager';
import Match from '@core/models/offer/Match';
import { SSE_BUFFER_TIME } from '@core/constants';
import { isEmpty as _isEmpty } from '@lodash';

interface SSEEventDTO {
    timestamp: number | null;
    data?: IEventDto;
    resourceId: string | null;
}

export interface SSEEventMessage {
    timestamp: number;
    event: Match | null;
    resourceId: string;
}

export default class SSEManager {
    private eventMapper = new EventMapper();
    private sseHostEndpoint: string;
    private prematchSubscription: Subscription;
    private outrightSubscription: Subscription;
    private liveMatchSubscription: Subscription;

    constructor(offerUrl: string) {
        this.sseHostEndpoint =  offerUrl;
    }

    private mapSSEMessage(e: SSEEventDTO) {
        let event: Match | null = null;
        if (e.data) {
            event = this.eventMapper.map(e.data);
            StructManager.getInstance().denormalizeEvent(event);
        }

        return {
            event,
            resourceId: e.resourceId!.split(':')[1],
            timestamp: e.timestamp
        };
    }
    private mapLiveEvents(events: SSEEventDTO[]) {
        const fn = (e: SSEEventDTO) => {
            let event: Match | null = null;
            if (e.data) {
                event = this.eventMapper.map(e.data);
                StructManager.getInstance().denormalizeEvent(event);
            }

            return {
                event,
                resourceId: e.resourceId!.split(':')[1],
                timestamp: e.timestamp
            };
        };
        return events.map(fn.bind(this));
    }

    private filterSSEMessage(e: SSEEventDTO) {
        return Boolean(e.resourceId) && Boolean(e.timestamp) && !_isEmpty(e.data) ;
    }

    public subscribeToEventChanges(
        eventId: number,
        callback: ({ resourceId, timestamp, event }: SSEEventMessage) => void
    ): Subscription {
        return createEventSourceObservable<SSEEventMessage>(
            ` ${ this.sseHostEndpoint }subscription/events/${ eventId } `
        )
            .pipe(
                filter((e: SSEEventDTO) => this.filterSSEMessage(e)),
                map((e) => this.mapSSEMessage(e))
            )
            .subscribe(callback as any);
    }

    public subscribeToAllEventChanges(
        callback: ({ resourceId, timestamp, event }: SSEEventMessage) => void
    ): Subscription {
        this.prematchSubscription = createEventSourceObservable<SSEEventMessage>(
            `${ this.sseHostEndpoint }subscription/events/prematch`
        )
            .pipe(
                filter((e: SSEEventDTO) => this.filterSSEMessage(e)),
                map((e) => this.mapSSEMessage(e))
            )
            .subscribe(callback as any);
        return this.prematchSubscription;
    }

    public subscribeToLiveEventChanges(
    callback: ({ resourceId, timestamp, event }: any) => void
  ): Subscription {
        this.liveMatchSubscription = createEventSourceObservable<any>(
      `${ this.sseHostEndpoint }subscription/events/live`
    )
      .pipe(
        filter((e: SSEEventDTO) => this.filterSSEMessage(e)),
        bufferTime(SSE_BUFFER_TIME),
        map((e) => this.mapLiveEvents(e))
      )
      .subscribe(callback as any);
        return this.liveMatchSubscription;
    }

    public unsubscribeFromStreams(): void {
        this.prematchSubscription.unsubscribe();
        this.outrightSubscription.unsubscribe();
        this.liveMatchSubscription.unsubscribe();
    }

    public subscribeToAllOutrightEventChanges(
        callback: ({ resourceId, timestamp, event }: SSEEventMessage) => void
    ): Subscription {
        this.outrightSubscription = createEventSourceObservable<SSEEventMessage>(
            `${ this.sseHostEndpoint }subscription/events/outrights`
        )
            .pipe(
                filter((e: SSEEventDTO) => this.filterSSEMessage(e)),
                map((e) => this.mapSSEMessage(e))
            )
            .subscribe(callback as any);

        return this.outrightSubscription;
    }
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--12!./src/core/services/offer/_private/SSEManager.ts